import searchBeneficiaryHomonyms from '@Application/Beneficiaries/Beneficiaries/searchBeneficiaryHomonyms';
import SearchBeneficiaryHomonyms from '@Application/Beneficiaries/Beneficiaries/Queries/SearchBeneficiaryHomonyms';
import useDebounce from '@UI/Hooks/useDebounce';
import useStrictAuthenticatedUser from '@UI/Providers/AuthenticatedUserProvider/useStrictAuthenticatedUser';
import { useDispatch } from '@verclam/synapse';
import * as React from 'react';

const HOMONYM_CHECK_DEBOUNCE_DELAY_IN_MILLISECONDS = 300;

/**
 * Non-blocking hint: dispatches `searchBeneficiaryHomonyms` — never `fetchBeneficiaries` — so the check
 * reads `beneficiariesStore.beneficiaries`, the very slot the beneficiary list reads from, is never
 * overwritten by a search fired while typing a name. The store keeps nothing of the response; the
 * *returned* action carries `beneficiaryIds`, which is all this hook needs. A failed check stays
 * silent: it must never itself surface as an error on a form this light.
 *
 * The record being edited is excluded by identity — `excludedBeneficiaryId` — never by a count
 * threshold: a threshold breaks the moment the edited record's name is changed to match an existing
 * homonym, since the response would then carry only that one other record's id while the edited record
 * (still under its old name in the database) never contributed a match to begin with.
 *
 * Scoped by the server's own tenant filter — a record the caller cannot access never surfaces here, an
 * accepted blind spot.
 */
function useBeneficiaryNameHomonymCheck(
	facilityId: number,
	lastName: string,
	excludedBeneficiaryId: number | null,
): boolean {
	const dispatch = useDispatch();
	const { user } = useStrictAuthenticatedUser();
	const accountId = user.account?.id ?? null;
	const [hasDuplicate, setHasDuplicate] = React.useState(false);
	const debouncedLastName = useDebounce(lastName.trim(), HOMONYM_CHECK_DEBOUNCE_DELAY_IN_MILLISECONDS);

	React.useEffect(() => {
		if (accountId === null || debouncedLastName.length === 0) {
			setHasDuplicate(false);
			return undefined;
		}

		let isMounted = true;

		dispatch(
			searchBeneficiaryHomonyms(
				new SearchBeneficiaryHomonyms(accountId, facilityId, debouncedLastName),
			),
		)
			.then((action) => {
				// The dispatch return type still allows `BENEFICIARY_HOMONYMS_SEARCH_FAILED` here (the union
				// the applicative declares), but a resolved promise only ever carries the success action —
				// the failure path always rejects instead. Narrowing on `type` keeps `payload` typed.
				if (!isMounted || action.type !== 'BENEFICIARY_HOMONYMS_SEARCHED') {
					return;
				}

				setHasDuplicate(
					action.payload.beneficiaryIds.some((identifier) => identifier !== excludedBeneficiaryId),
				);
			})
			.catch(() => {
				// A failed homonym check is a comfort, never an error: stay silent rather than surface
				// anything on a form that does not need this hint to function.
				if (isMounted) {
					setHasDuplicate(false);
				}
			});

		return () => {
			isMounted = false;
		};
	}, [dispatch, accountId, facilityId, debouncedLastName, excludedBeneficiaryId]);

	return hasDuplicate;
}

export default useBeneficiaryNameHomonymCheck;
